import React, {useEffect, useState} from "react";
import {CheckboxInput, Fieldset, TextButton, TextInput} from "atmosfair-ui";
import {validateCompanyName} from "../../forms/utils";
import {theme} from "../../../styles/theme";
import {useTranslation} from "react-i18next";
import {useInviteCompanyAdminMutation} from "../../../redux/reducer/api";
import {GENERAL_BACKEND_RESPONSE} from "../../../api/model/common";
import SectionTitle from "../section-title/SectionTitle";
import NameEmailForm, {NameEmailProps} from "../../forms/name-email-form/NameEmailForm";
import {RESET_FORM_STARTING_NUMBER, VALIDATE_FORM_STARTING_NUMBER} from "../../../constants/default-values";
import {useEffectAllDepsChange} from "../../../hooks/custom-use-effect";
import {AgencyProps} from "../../../api/model/agency";
import {AgenciesCompanyFormProps} from "../../forms/agencies-company-form/AgenciesCompanyForm";
import AgenciesCompanyDataGrid from "../agencies-company/agencies-company-data-grid/AgenciesCompanyDataGrid";
import AllUsersDataGrid from "../all-users-data-grid/AllUsersDataGrid";
import {UserProps} from "../../../api/model/user";

interface KlimaLinkAdministrationProps {
    agencies: AgencyProps[],
    allUsers: AgenciesCompanyFormProps['allUsers'],
    userData: UserProps
}

interface FormProps {
    companyName: string
    isMember: boolean,
}

interface FormErrorProps {
    companyNameError: boolean,
}

const KlimaLinkAdministration = ({
                                     agencies,
                                     allUsers,
                                     userData
                                 }: KlimaLinkAdministrationProps) => {
    const [formData, setFormData] = useState<FormProps>({companyName: '', isMember: false})
    const [validateForm, setValidateForm] = useState(VALIDATE_FORM_STARTING_NUMBER)
    const [resetForm, setResetForm] = useState(RESET_FORM_STARTING_NUMBER)
    const [nameEmailData, setNameEmailData] = useState<NameEmailProps | null>(null)
    const [formErrorData, setFormErrorData] = useState<FormErrorProps>({companyNameError: false})
    const {companyName, isMember} = formData
    const {companyNameError} = formErrorData
    const {i18n} = useTranslation()
    const currentLanguage = i18n.resolvedLanguage
    const disableNotOnProduction = process.env.REACT_APP_INSTANCE === "local" ? false : process.env.REACT_APP_INSTANCE !== "prod"

    const [inviteCompanyAdmin, {
        data: inviteCompanyAdminResponse,
        isLoading: isLoadingInviteCompanyAdmin,
    }] = useInviteCompanyAdminMutation()

    useEffect(() => {
        if (inviteCompanyAdminResponse?.status === GENERAL_BACKEND_RESPONSE.success) _resetForm()
    }, [inviteCompanyAdminResponse])

    useEffectAllDepsChange(() => {
        if (validateForm > VALIDATE_FORM_STARTING_NUMBER) {
            const validForm = _validateForm()
            if (validForm && nameEmailData) {
                const {firstName, lastName, email} = nameEmailData
                inviteCompanyAdmin({
                    firstName,
                    lastName,
                    email,
                    companyName,
                    isMember,
                    language: currentLanguage ?? "de",
                })
            }
        }
    }, [nameEmailData, validateForm])

    const _updateFormData = <K extends keyof FormProps>(key: K, payload: FormProps[K]) => setFormData({
        ...formData,
        [key]: payload
    })

    const _resetForm = () => {
        setNameEmailData(null)
        setValidateForm(VALIDATE_FORM_STARTING_NUMBER)
        setResetForm(resetForm + 1)
        setFormData({companyName: "", isMember: false})
    }

    const _validateForm = () => {
        const validCompanyName = validateCompanyName(companyName)
        setFormErrorData({companyNameError: !validCompanyName})
        return [validCompanyName].every(Boolean)
    }

    const _setValidateForm = () => setValidateForm(validateForm + 1)

    return <>
        <Fieldset
            fieldsetStyles={{marginBottom: theme.spacer.large, minWidth: "unset"}}
            showLegend={false} legend={"LABELS.SETTINGS.KLIMALINK_ADMINISTRATION"}>
            <SectionTitle
                isLoading={isLoadingInviteCompanyAdmin}
                title={"SCREENS.SETTINGS.INVITE_COMPANY_ADMIN"}/>
            <div className={"form-row"}>
                <TextInput
                    aria-errormessage={"ERRORS.COMMON.NAME"}
                    error={companyNameError}
                    onBlur={event => _updateFormData('companyName', event.target.value.trim())}
                    onChange={event => _updateFormData('companyName', event.target.value)}
                    containerStyle={{flex: 1}}
                    required={true}
                    value={companyName}
                    placeholder={"GENERAL.COMPANY_NAME"}
                    label={"GENERAL.COMPANY_NAME"}/>
                <NameEmailForm
                    resetForm={resetForm}
                    firstNameRequired={true}
                    lastNameRequired={true}
                    emailRequired={true}
                    validateForm={validateForm}
                    onFormIsValid={setNameEmailData}/>
                <CheckboxInput
                    hideLabel={false}
                    label={"LABELS.SETTINGS.IS_MEMBER"}
                    checked={isMember}
                    onChange={() => _updateFormData("isMember", !isMember)}
                />
            </div>
            <div style={{display: "inline-block"}}>
                <TextButton
                    disabled={disableNotOnProduction}
                    tooltip={disableNotOnProduction ? "TOOLTIP.SETTINGS.INVITE_USERS_ON_STAGING" : undefined}
                    containerStyle={{marginTop: theme.spacer.large}}
                    onClick={() => _setValidateForm()}>GENERAL.INVITE</TextButton>
            </div>
        </Fieldset>
        <AllUsersDataGrid
            userData={userData}
            allUsers={allUsers}/>
        <AgenciesCompanyDataGrid
            allUsers={allUsers}
            isCompanyAdmin={true}
            agencies={agencies}
            whichType={"agencies"}
            displayInSelectAgencyModal={false}
            isKlimaLinkAdmin={true}/>
    </>
}

export default KlimaLinkAdministration